import Colors from '@constants/Colors';
import { QueryKeys, useGetCommunitiesInfinite } from '@sunrise-ui/api-client';
import type { Community } from '@sunrise-ui/api/profile';
import { CommunityResult, withAuthGate } from '@sunrise-ui/composites';
import {
  CacheAvailabilityProvider,
  Loader,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useMemo } from 'react';
import { FlatList, RefreshControl, StyleSheet, View } from 'react-native';

const { communitiesKey, byIdKey } = QueryKeys.communities;

function CommunitiesPage() {
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const {
    data,
    isLoading,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useGetCommunitiesInfinite(40);

  const communities = useMemo(
    () =>
      (data?.pages.flatMap((page) => page?.results ?? []) ?? []).filter(
        (c): c is Community & { id: string } => Boolean(c.id),
      ),
    [data],
  );

  const organizationCommunities = useMemo(
    () => communities.filter((c) => c.community_type === 'organization'),
    [communities],
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLoadMore = async () => {
    if (hasNextPage && !isFetching) {
      await fetchNextPage();
    }
  };

  const numColumns = isMobile ? 2 : 4;

  const renderItem = ({ item }: { item: Community & { id: string } }) => (
    <View style={styles.communityCard}>
      <CommunityResult
        key={item.id}
        communityId={item.id}
        size="small"
        shouldNavigateOnClick={true}
        isOneLineText={false}
        centerText={true}
        style={styles.communityContent}
      />
    </View>
  );

  if (isLoading && !communities.length) {
    return (
      <View style={styles.loadingContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CacheAvailabilityProvider
        queryKeys={new Set([[communitiesKey, byIdKey]])}
      >
        <FlatList
          data={organizationCommunities}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          numColumns={numColumns}
          key={numColumns}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.5}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor={Colors.dark.text}
              colors={[Colors.dark.text]}
              progressBackgroundColor={Colors.dark.opacity20}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <TextReg style={styles.emptyText}>No communities found</TextReg>
            </View>
          }
          ListFooterComponent={
            isFetchingNextPage && organizationCommunities.length > 0 ? (
              <View style={styles.footerLoader}>
                <Loader />
              </View>
            ) : null
          }
        />
      </CacheAvailabilityProvider>
    </View>
  );
}

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Colors.dark.purple0,
    },
    communityCard: {
      backgroundColor: Colors.dark.opacity05,
      borderRadius: 12,
      padding: 12,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: isMobile ? 100 : 120,
      margin: isMobile ? 4 : 6,
      flex: 1,
    },
    communityContent: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      width: '100%',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: Colors.dark.purple0,
    },
    listContent: {
      paddingHorizontal: isMobile ? 8 : isTablet ? 12 : 16,
      paddingVertical: 16,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 40,
    },
    emptyText: {
      fontSize: 16,
      color: Colors.dark.text,
      opacity: 0.7,
      textAlign: 'center',
    },
    footerLoader: {
      paddingVertical: 20,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

export default withAuthGate(CommunitiesPage);
